import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Camera, Sparkles, Users, Calendar, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import './Home.css';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_eventbooth-app/artifacts/kfxavvcq_1768280331613.png';

const features = [
  {
    icon: Camera,
    title: '360° Photo Booths',
    description: 'Capture every angle with stunning slow-motion videos that your guests will love.',
  },
  {
    icon: Sparkles,
    title: 'Mirror Booths',
    description: 'Interactive touchscreen magic mirrors with animations and custom overlays.',
  },
  {
    icon: Users,
    title: 'Open Air Booths',
    description: 'Perfect for large groups with premium backdrops and professional lighting.',
  },
  {
    icon: Calendar,
    title: 'Easy Booking',
    description: 'Book your event in minutes with our seamless online booking system.',
  },
];

const stats = [
  { value: '500+', label: 'Events' },
  { value: '50k+', label: 'Photos Taken' },
  { value: '100%', label: 'Satisfaction' },
  { value: '5★', label: 'Rating' },
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [reviewsData, packagesData] = await Promise.all([
        api.getReviews(),
        api.getPackages(),
      ]);
      setReviews(reviewsData.slice(0, 3));
      setPackages(packagesData.slice(0, 3));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <img src={HERO_IMAGE} alt="Luxe Lens Events booth setup" />
          <div className="hero-overlay" />
        </div>
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-badge">Premium Photo Booth Experience</span>
            <h1 className="hero-title">
              Creating Moments<br />
              <span className="text-gold">Beyond the Camera</span>
            </h1>
            <p className="hero-description">
              Elevate your event with our luxury photo booth rentals. From weddings to corporate events, 
              we bring unforgettable experiences to life.
            </p>
            <div className="hero-actions">
              <Link to="/book" className="btn btn-primary">
                Book Your Event
                <ArrowRight size={18} />
              </Link>
              <Link to="/services" className="btn btn-secondary">
                View Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Why Choose Us</span>
            <h2 className="section-title">Premium Photo Booth Experiences</h2>
            <p className="section-description">
              We provide top-of-the-line equipment and professional service to make your event unforgettable.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="feature-icon">
                  <feature.icon size={28} />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      {packages.length > 0 && (
        <section className="section packages-section">
          <div className="container">
            <div className="section-header">
              <span className="section-subtitle">Our Packages</span>
              <h2 className="section-title">Choose Your Experience</h2>
            </div>
            <div className="packages-grid">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  className={`package-card ${pkg.popular ? 'popular' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {pkg.popular && <span className="package-badge">Most Popular</span>}
                  <h3 className="package-name">{pkg.name}</h3>
                  <div className="package-price">
                    <span className="price-amount">${pkg.price}</span>
                    <span className="price-duration">/ {pkg.duration}</span>
                  </div>
                  <p className="package-description">{pkg.description}</p>
                  <ul className="package-features">
                    {pkg.features?.slice(0, 5).map((feature, i) => (
                      <li key={i}>
                        <CheckCircle size={16} className="text-gold" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/book" className="btn btn-primary w-full">
                    Select Package
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/services" className="btn btn-ghost">
                View All Packages
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="section reviews-section">
          <div className="container">
            <div className="section-header">
              <span className="section-subtitle">Testimonials</span>
              <h2 className="section-title">What Our Clients Say</h2>
            </div>
            <div className="reviews-grid">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  className="review-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="review-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        className={i < review.rating ? 'star-filled' : 'star-empty'}
                      />
                    ))}
                  </div>
                  <p className="review-text">"{review.review_text}"</p>
                  <div className="review-author">
                    <div className="review-avatar">
                      {review.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="review-name">{review.customer_name}</p>
                      <p className="review-event">{review.event_type}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/reviews" className="btn btn-ghost">
                See All Reviews
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Create Unforgettable Memories?</h2>
            <p className="cta-description">
              Book your luxury photo booth experience today and make your event extraordinary.
            </p>
            <Link to="/book" className="btn btn-primary">
              Book Now
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
